<template>
  <div>
    <v-alert
      v-if="errorMsg"
      class="error-msg"
      :text="errorMsg"
      type="error"
      variant="outlined"
      :closable="true"></v-alert>
    <v-container v-if="pageError" class="mt-8" data-testid="clinical-review-not-found">
      <EmptyState variant="error" :message="pageError" action-label="Go Back" @action="onBack" />
    </v-container>
    <div v-else class="d-flex flex-column align-center mt-8 intake-detail">
      <v-container :fluid="true" class="lh--container-wide review-container">
        <v-row>
          <v-col cols="12">
            <div class="intake-detail__header">
              <h1 class="intake-detail__title">Medical Necessity</h1>
              <span class="intake-detail__stage-chip">
                <span class="intake-detail__stage-dot" />
                Medical Necessity
              </span>
            </div>
            <ReadOnlyBanner v-if="isReadOnlyDevice" />
            <ReadOnlyBanner v-if="intakeStore.lockViewer" icon="mdi-account-lock-outline" :text="lockBannerText">
              <template v-if="lockFree && !isReadOnlyDevice" #action>
                <v-btn size="small" color="primary" variant="flat" @click="takeOverEditing">Take over editing</v-btn>
              </template>
            </ReadOnlyBanner>
            <v-alert
              v-if="closedStep"
              type="info"
              variant="tonal"
              density="compact"
              class="mb-4"
              text="Medical necessity is not open on this order. It is read only." />
            <IntakeStepNotice
              v-if="worklistEnabled && order"
              step="clinical_review"
              :status="order.status"
              :order-id="orderId"
              :order-info="order.orderInfo"
              :review-order-info="order.reviewOrderInfo" />
          </v-col>
          <v-col cols="12">
            <v-row no-gutters>
              <v-col v-if="!showLoader" cols="12" md="7">
                <PatientBar
                  :patient="patient"
                  :patient-guess="order?.patientGuess"
                  :client-company="order?.clientCompany.id"
                  @highlight-bounding-box="onPatientGuessHighlight" />
              </v-col>
              <v-col v-if="!showLoader && !noDocument" cols="12" md="5">
                <ClassificationBar :patient-guess="order?.patientGuess" />
              </v-col>
              <v-container v-if="showLoader">
                <v-skeleton-loader type="text@4" />
              </v-container>
            </v-row>
          </v-col>
          <v-col cols="12">
            <v-skeleton-loader v-if="showLoader || !previewReady" type="image@8" />
            <PagePreview
              v-if="!showLoader && previewReady"
              ref="previewRef"
              :no-document="noDocument"
              :no-document-text="emptyEvidenceText"
              :read-only="!canAct"
              :closed-step="closedStep"
              :workflow-actions="canAct"
              :form-deletable="!isManualEntry"
              :order-form="order.orderForm"
              :insurance-cards="order.insuranceCard ? [order.insuranceCard] : []"
              :stage="intakeStatus"
              :is-quick-start-submission="!!order?.isQuickStartSubmission"
              @data-form-selected="onDataFormSelected"
              @attribute-edit="onAttributeEdit"
              @snooze="onSnooze"
              @archive="onArchive">
              <template #before>
                <v-col v-if="documentsShown" cols="12">
                  <v-card class="pa-4">
                    <h2 class="text-h6 mb-1">Documents</h2>
                    <p class="text-body-2 text-medium-emphasis mb-4">
                      Our medical necessity check reads every file. Re-run the review after adding new ones.
                    </p>
                    <UploadDocuments :patient="patient" :case-id="order.caseId" @uploaded="onDocumentsUploaded" />
                    <v-alert
                      v-if="documentsNotice"
                      :type="documentsNotice.type"
                      variant="tonal"
                      density="compact"
                      class="mt-4"
                      :text="documentsNotice.text" />
                    <div v-if="rerunShown" class="mt-4">
                      <v-alert
                        v-if="documentsRerunError"
                        type="error"
                        variant="tonal"
                        density="compact"
                        class="mb-3"
                        :text="documentsRerunError" />
                      <div class="d-flex justify-end">
                        <v-btn
                          color="primary"
                          variant="flat"
                          prepend-icon="mdi-refresh"
                          :loading="rerunningReview"
                          @click="onRerunDocumentsReview">
                          Re-run Policy Review
                        </v-btn>
                      </div>
                    </div>
                  </v-card>
                </v-col>
                <v-col v-if="shouldShowPolicyReview" cols="12">
                  <PolicyReviewCriteriaCard
                    :key="policyCardKey"
                    :order="order"
                    :readonly="viewOnly"
                    :current-page-id="currentPage?.id"
                    :page-manager="pageManager"
                    @highlight-evidence="onHighlightEvidence" />
                </v-col>
                <v-col v-if="shouldShowReimbursementTable" cols="12">
                  <ReimbursementDataCard :order="order" />
                </v-col>
              </template>
              <template #after>
                <v-col v-if="coverageShown" cols="12">
                  <StepCoverageCard :coverage="stepCoverage" :selected="false" />
                </v-col>
              </template>
            </PagePreview>
            <BottomBar v-if="!isReadOnlyDevice" :intake-status="intakeStatus" />
            <div v-if="!isReadOnlyDevice" class="intake-actionbar">
              <v-btn
                variant="outlined"
                color="primary"
                rounded
                prepend-icon="mdi-arrow-left"
                :loading="showLoader"
                @click="onBack">
                Go Back
              </v-btn>
              <v-btn
                v-if="faxActions"
                variant="tonal"
                color="primary"
                rounded
                prepend-icon="mdi-card-account-details-outline"
                :loading="showLoader"
                @click="onChangeInsurance">
                Change Insurance
              </v-btn>
              <v-btn
                v-if="faxActions"
                variant="tonal"
                color="primary"
                rounded
                prepend-icon="mdi-hand-heart-outline"
                :active="referralThankYouVisible"
                :loading="showLoader"
                @click="onReferralThankYou">
                Referral Thank You
              </v-btn>
              <v-btn
                v-if="faxActions"
                variant="tonal"
                color="primary"
                rounded
                prepend-icon="mdi-file-question-outline"
                :active="missingInformationVisible"
                :loading="showLoader"
                @click="onRequestMissingInformation">
                Request Missing Information
              </v-btn>
              <v-btn
                v-if="carequalityEnabled && !viewOnly"
                :style="order?.isQuickStartSubmission ? { order: 1 } : null"
                variant="tonal"
                color="primary"
                rounded
                prepend-icon="mdi-lan-connect"
                :loading="showLoader"
                @click="onSearchNetworkRecords">
                Search Carequality network
                <v-badge
                  v-if="networkSearchInFlight || networkRecordsFound"
                  :content="networkRecordsFound || ''"
                  :color="networkSearchInFlight ? 'warning' : 'success'"
                  :class="{ 'network-badge--running': networkSearchInFlight }"
                  inline
                  :title="networkSearchInFlight ? 'Search running' : 'Records found on the network'" />
                <v-badge
                  v-if="networkDocumentCount"
                  :content="networkDocumentCount"
                  color="accent"
                  inline
                  title="Documents attached to this medical necessity review" />
              </v-btn>
              <div class="intake-actionbar__spacer" />
              <v-btn
                v-if="(opensCreateSubmission || !order?.isQuickStartSubmission) && !viewOnly"
                color="primary"
                variant="flat"
                rounded
                append-icon="mdi-arrow-right"
                :loading="showLoader"
                :disabled="buttonDisabled"
                @click="onStartPriorAuth">
                {{ submitLabel }}
              </v-btn>
            </div>
            <v-expand-transition>
              <div v-if="missingInformationVisible" class="intake-actionbar-expansion">
                <OutgoingComposer
                  v-if="order"
                  v-model:send-method="sendMethod"
                  heading="Message to Provider"
                  :intake-order="order"
                  :method-toggle-visible="sendEmailEnabled"
                  :fax-number="order.orderForm?.providerFax"
                  :email-subject="missingInformationSubject"
                  :template-types="MISSING_INFORMATION_TEMPLATE_TYPES"
                  :prepare-request="markMissingInformationRequest"
                  @fax-complete="onMissingInformationComplete"
                  @email-complete="onMissingInformationComplete" />
              </div>
            </v-expand-transition>
            <v-expand-transition>
              <div v-if="referralThankYouVisible" class="intake-actionbar-expansion">
                <OutgoingFaxEditor
                  v-if="order"
                  title="Referral Thank You Message"
                  :types="[TEMPLATE_TYPES.INTAKE_REFERAL_THANK_YOU]"
                  :intake-order="order"
                  :to-fax-number="order.orderForm?.providerFax"
                  @on-complete="onReferralThankYou" />
              </div>
            </v-expand-transition>
          </v-col>
        </v-row>
        <v-row v-if="order">
          <v-col cols="12">
            <!-- Voice AI panel (missing-clinicals agent) is deliberately not mounted here yet; see LAM-5532. -->
            <BenefitsVerificationCard
              v-if="worklistEnabled"
              :anchor="{ type: 'intake_order', id: orderId }"
              surface="CLINICAL_REVIEW"
              :prefill="benefitsPrefill"
              :readonly="viewOnly || !benefitsWritable"
              :voice="false"
              @changed="onBenefitsChanged" />
            <IntakeTagBar
              :tags="order.statusTags"
              :tag-defs="availableTags"
              :tag-due-dates="order.tagDueDates"
              entity-type="order"
              :entity-id="orderId"
              @update:tags="(t) => (order.statusTags = t)"
              @saved="auditLogListRef?.showLatest()"
              @update:tag-due-dates="(d) => (order.tagDueDates = d)" />
            <AssigneeBar
              :assignees="order.assignees || []"
              entity-type="order"
              :entity-id="orderId"
              @update:assignees="(a) => (order.assignees = a)" />
            <GroupBar
              :assigned-groups="order.assignedGroups || []"
              entity-type="order"
              :entity-id="orderId"
              @update:assigned-groups="(g) => (order.assignedGroups = g)" />
            <SubstatusBar entity-type="order" :entity-id="orderId" />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <AuditLogList ref="auditLogListRef" :intake-order-id="orderId" :tag-defs="availableTags" />
          </v-col>
        </v-row>
      </v-container>
    </div>

    <v-dialog v-model="showArchiveReasonDialog">
      <ArchiveNote :intake-order="order" @on-archived="onArchived" />
    </v-dialog>
    <NetworkRecordsDialog
      v-if="carequalityEnabled && patient"
      ref="networkRecordsRef"
      v-model="networkRecordsVisible"
      :order-id="orderId"
      :patient-id="patient.id"
      :readonly="viewOnly"
      @selection-saved="networkDocumentCount = $event"
      @evidence-changed="onEvidenceChanged"
      @state-changed="onNetworkStateChanged" />
    <MedicalBenefitDialog
      v-model="medicalBenefitDialogVisible"
      :benefit-type="orderBenefitType"
      :loading="showLoader"
      :error-message="errorMsg"
      @confirm-pharmacy="onConfirmPharmacy"
      @mark-pa-not-required="onMarkPaNotRequired"
      @submit-external="onSubmitExternal" />
  </div>
</template>

<script setup>
import { useMarkCaseOpened } from "@/composables/useCaseReadState";
import AuditLogList from "@/components/AuditLogList.vue";
import { useDate } from "vuetify";
import { onMounted, onUnmounted, ref, computed, watch } from "vue";
import { storeToRefs } from "pinia";
import { useRoute, useRouter } from "vue-router";
import { listReturnTarget } from "@/composables/useListReturn";
import { useIntakeBenefitsCard } from "@/composables/useIntakeBenefitsCard";
import { POLL_TIMED_OUT, useTaskPoll } from "@/composables/useTaskPoll";
import { intakeService } from "@/services/intakeService";
import { carequalityService } from "@/services/carequalityService";
import { clinicalReviewSubmitService, SubmitAbandoned, submitFailure } from "@/services/clinicalReviewSubmitService";
import ArchiveNote from "@/components/ArchiveNote.vue";
import PagePreview from "@/components/intakeReview/PagePreview.vue";
import OutgoingFaxEditor from "@/components/OutgoingFaxEditor.vue";
import OutgoingComposer from "@/components/OutgoingComposer.vue";
import PatientBar from "@/components/intakeReview/PatientBar.vue";
import BottomBar from "@/components/intakeReview/BottomBar.vue";
import ClassificationBar from "@/components/intakeReview/ClassificationBar.vue";
import ReimbursementDataCard from "@/components/ReimbursementDataCard.vue";
import PolicyReviewCriteriaCard from "@/components/PolicyReviewCriteriaCard.vue";
import IntakeTagBar from "@/components/dashboard/IntakeTagBar.vue";
import AssigneeBar from "@/components/dashboard/AssigneeBar.vue";
import GroupBar from "@/components/dashboard/GroupBar.vue";
import SubstatusBar from "@/components/dashboard/SubstatusBar.vue";
import MedicalBenefitDialog from "@/components/intakeReview/MedicalBenefitDialog.vue";
import NetworkRecordsDialog from "@/components/NetworkRecordsDialog.vue";
import UploadDocuments from "@/components/UploadDocuments.vue";
import {
  DOCUMENT_CLASSIFICATIONS,
  INTAKE_STAGE,
  IntakeDocumentPage,
  IntakeOrder,
  clearIntakeSaveErrorHandler,
  setIntakeSaveErrorHandler,
} from "@/models/intake";
import { useIntakeStore } from "@/stores/intakeStore";
import { TEMPLATE_TYPES } from "@/models/templates";
import { useRequirementsStore } from "@/stores/requirementsStore";
import { useIntakeListFiltersStore } from "@/stores/intakeListFiltersStore";
import { handleFormValidationErrors } from "@/utils/validators";
import { getPARoute } from "@/utils/paRoutes";
import { useOrderPriorAuthHidden } from "@/composables/usePriorAuthHidden";
import ReadOnlyBanner from "@/components/dashboard/ReadOnlyBanner.vue";
import EmptyState from "@/components/dashboard/EmptyState.vue";
import IntakeStepNotice from "@/components/intakeReview/IntakeStepNotice.vue";
import BenefitsVerificationCard from "@/components/benefits/BenefitsVerificationCard.vue";
import StepCoverageCard from "@/components/intakeReview/StepCoverageCard.vue";
import { useEditLock } from "@/composables/useEditLock";
import { useResponsive } from "@/composables/useResponsive";

const intakeStore = useIntakeStore();
const requirementsStore = useRequirementsStore();
const filtersStore = useIntakeListFiltersStore();
const dateAdapter = useDate();
const { isReadOnlyDevice } = useResponsive();
const showLoader = ref(true);
const intakeStatus = ref("Medical Necessity");
const pageManager = intakeStore.pageManager;
const currentPage = computed(() => intakeStore.pageManager.currentPage);
const route = useRoute();
useMarkCaseOpened("intakeOrderId", () => route.params.id);
const orderId = Number.parseInt(route.params.id);
const order = computed(() => intakeStore.intakeOrder);
const patient = computed(() => order.value?.patient ?? null);
const router = useRouter();
const errorMsg = ref(undefined);
const pageError = ref(null);
const missingInformationVisible = ref(false);
// One array for the page's life: the editors refetch templates (and reset the message) whenever `types` changes.
const MISSING_INFORMATION_TEMPLATE_TYPES = [TEMPLATE_TYPES.INTAKE_MISSING_INFORMATION];
const sendMethod = ref("fax");
const referralThankYouVisible = ref(false);
const buttonDisabled = ref(false);
const showArchiveReasonDialog = ref(false);
const medicalBenefitDialogVisible = ref(false);
const networkRecordsVisible = ref(false);
const networkRecordsRef = ref(null);
const networkDocumentCount = ref(0);
const networkSearchInFlight = ref(false);
const networkRecordsFound = ref(0);
const networkSelectionSaving = ref(false);
const policyCardKey = ref(0);
const EVIDENCE_POLL_MS = 5000;
const DOCUMENT_READY_POLL_MS = 3000;
// Leaves room for one task retry, which waits SPLIT_LOCK_SECONDS (120s); no limit fits every batch.
const DOCUMENT_READY_LIMIT_MS = 300000;
const EVIDENCE_POLL_LIMIT_MS = 120000;
const RERUN_FAILED = "The records are saved, but the policy review did not restart.";
let evidencePoll = null;
let evidenceGeneration = 0;
let pagesRequest = 0;
const pagesLoaded = ref(false);
const lockSettled = ref(false);
const bvStep = ref(null);
const {
  holds: holdsLock,
  free: lockFree,
  bannerText: lockBannerText,
  start: startEditLock,
  stop: stopEditLock,
  takeOver,
  demote,
} = useEditLock(storeToRefs(intakeStore).lockViewer);
const ARCHIVED_STATUSES = [INTAKE_STAGE.ARCHIVED, "DISCONTINUE_PROCESSING"];
const COVERAGE_BENEFITS = { pharmacy_benefits: "PHARMACY", medical_benefits: "MEDICAL" };
const isManualEntry = computed(() => Boolean(order.value?.orderForm) && !order.value.orderForm.intakeDocumentKey);
const noDocument = computed(() => isManualEntry.value && pagesLoaded.value && !currentPage.value);
const closedStep = computed(
  () =>
    isManualEntry.value &&
    order.value.status !== INTAKE_STAGE.CLINICAL_REVIEW &&
    !ARCHIVED_STATUSES.includes(order.value.status)
);
const takesLock = computed(
  () => isManualEntry.value && !isReadOnlyDevice.value && !intakeStore.readOnly && !closedStep.value
);
const canAct = computed(() => !closedStep.value && (!isManualEntry.value || holdsLock.value));
const viewOnly = computed(() => !canAct.value || intakeStore.isReadOnly || isReadOnlyDevice.value);
const decidesPa = computed(() => Boolean(order.value?.reviewOrderInfo) && !order.value?.orderInfo);
const priorAuthHidden = useOrderPriorAuthHidden(order);
const opensCreateSubmission = computed(
  () => requirementsStore.clientConfig?.intake?.clinicalReviewOpensCreateSubmission === true && !priorAuthHidden.value
);
const draftsPa = computed(() => opensCreateSubmission.value && !decidesPa.value);
const submitLabel = computed(() => {
  if (decidesPa.value) return "Decide this prior authorization";
  if (draftsPa.value) return "Create PA Submission";
  return "No Missing Information, Start Prior Authorization";
});
const mayEditForm = computed(() => !viewOnly.value);
const faxActions = computed(() => !viewOnly.value && !isManualEntry.value && !order.value?.isQuickStartSubmission);
const previewReady = computed(() =>
  Boolean(
    order.value &&
      requirementsStore.clientConfig &&
      (currentPage.value || noDocument.value) &&
      (!isManualEntry.value || lockSettled.value)
  )
);
const stepCoverage = computed(() => bvStep.value?.coverage ?? null);
const coverageShown = computed(() => Boolean(order.value && !order.value.insuranceCard && stepCoverage.value));
const coverageAsCard = () =>
  coverageShown.value
    ? { payorName: stepCoverage.value.insuranceProvider, memberId: stepCoverage.value.memberId }
    : null;

function onNetworkStateChanged({ inFlight, recordCount, saving }) {
  networkSearchInFlight.value = inFlight;
  networkRecordsFound.value = recordCount;
  networkSelectionSaving.value = saving;
}

const waitForNetworkSelectionSave = () =>
  new Promise((resolve) => {
    if (!networkSelectionSaving.value) {
      resolve();
      return;
    }
    const stop = watch(networkSelectionSaving, (saving) => {
      if (!saving) {
        stop();
        resolve();
      }
    });
  });

const stopEvidencePoll = () => {
  evidenceGeneration += 1;
  clearTimeout(evidencePoll);
  evidencePoll = null;
};

const pollEvidence = (deadline, generation) => {
  evidencePoll = setTimeout(async () => {
    let processing = 0;
    try {
      ({ processing } = await carequalityService.getSelection(orderId));
    } catch (ex) {
      errorMsg.value = `Error checking the network records: ${ex.message ?? ex}`;
    }
    if (unmounted || generation !== evidenceGeneration) return;
    if (processing > 0 && Date.now() < deadline) {
      pollEvidence(deadline, generation);
      return;
    }
    evidencePoll = null;
    await fetchPages();
  }, EVIDENCE_POLL_MS);
};

const onEvidenceChanged = async ({ processing, review }) => {
  if (unmounted) return;
  stopEvidencePoll();
  const generation = evidenceGeneration;
  if (review === "queued") policyCardKey.value += 1;
  if (review === null) {
    errorMsg.value = viewOnly.value ? RERUN_FAILED : `${RERUN_FAILED} Open Network Records and close it to try again.`;
  } else if (errorMsg.value?.startsWith(RERUN_FAILED)) {
    errorMsg.value = "";
  }
  await fetchPages();
  if (processing > 0 && !unmounted && generation === evidenceGeneration) {
    pollEvidence(Date.now() + EVIDENCE_POLL_LIMIT_MS, generation);
  }
};

const onSearchNetworkRecords = async () => {
  if (networkRecordsFound.value || networkSearchInFlight.value) {
    networkRecordsVisible.value = true;
    return;
  }
  await networkRecordsRef.value?.startSearch();
};
const restrictToPharmacy = computed(() => requirementsStore.clientConfig?.intake?.restrictPriorAuthToPharmacy ?? false);
const orderBenefitType = computed(
  () =>
    order.value?.insuranceCard?.benefitType ??
    (coverageShown.value ? COVERAGE_BENEFITS[stepCoverage.value.benefitType] : null) ??
    null
);
const availableTags = computed(() => requirementsStore.clientConfig?.intake?.availableTags || []);
const worklistEnabled = computed(() => requirementsStore.clientConfig?.worklistEnabled ?? false);
const { benefitsPrefill, benefitsWritable, onBenefitsChanged } = useIntakeBenefitsCard({
  orderId: () => orderId,
  order: () => order.value,
  insuranceCard: () => order.value?.insuranceCard ?? coverageAsCard(),
  onRefreshed: () => auditLogListRef.value?.refresh(),
  onError: (message) => (errorMsg.value = message),
});
const previewRef = ref(null);
const auditLogListRef = ref(null);

// Policy evidence boxes are passage-level, so they need less enlargement than intake's single-line
// default (1.5). 1 = the raw box plus the renderer's halo. Raise if highlights read too tight.
const POLICY_HIGHLIGHT_SCALE = 1;

// pageId -> page-manager position; +1 because onHighlightBoundingBox expects a 1-based pageNumber.
// sourceForm=null makes resolveHighlightPageNumber pass it through unchanged (already in page-manager space).
const onPatientGuessHighlight = (data) => {
  previewRef.value?.onHighlightBoundingBox(data, order.value?.orderForm);
};

const onHighlightEvidence = ({ pageId, boundingBox, fieldKey }) => {
  const idx = pageManager.getPageNumber(pageId);
  if (idx === -1) return;
  previewRef.value?.onHighlightBoundingBox(
    { pageNumber: idx + 1, boundingBox, fieldKey, scaleFactor: POLICY_HIGHLIGHT_SCALE },
    null
  );
};

let unmounted = false;

const settleLock = async () => {
  if (takesLock.value) {
    const formId = order.value.orderForm.id;
    await startEditLock(`intake_order_form:${formId}`, intakeService.orderFormLock(formId)).catch(() => {
      errorMsg.value = "Could not take the edit lock. Reload the page.";
    });
  }
  if (!unmounted) lockSettled.value = true;
};

watch(takesLock, (takes) => {
  if (!lockSettled.value) return;
  if (!takes) stopEditLock();
  else if (!holdsLock.value) settleLock();
});

const onSaveError = (e) => {
  const wasEditing = !intakeStore.lockViewer;
  if (!demote(e)) throw e;
  if (wasEditing) errorMsg.value = "This record is now being edited by another user.";
};

const onBeforeUnload = () => {
  if (mayEditForm.value) order.value?.saveUpdates();
};

onMounted(async () => {
  window.addEventListener("beforeunload", onBeforeUnload);
  intakeStore.reset();
  intakeStore.stage = INTAKE_STAGE.CLINICAL_REVIEW;
  await fetchIntakeOrder();
  if (unmounted || !order.value) return;
  await requirementsStore.refreshClientConfig(order.value.clientCompany?.id);
  if (unmounted) return;
  if (isManualEntry.value) setIntakeSaveErrorHandler(onSaveError);
  await Promise.all([settleLock(), fetchPages()]);
  if (!unmounted) resumePendingSubmit();
});

onUnmounted(() => {
  unmounted = true;
  stopEvidencePoll();
  clearPolls();
  window.removeEventListener("beforeunload", onBeforeUnload);
  clearIntakeSaveErrorHandler(onSaveError);
  requirementsStore.refreshCurrentUserClientConfig();
});

const takeOverEditing = async () => {
  try {
    if (!(await takeOver())) return;
  } catch (e) {
    errorMsg.value = "Could not take over editing. Try again.";
    return;
  }
  errorMsg.value = "";
  await fetchIntakeOrder();
};

const onChangeInsurance = async () => {
  try {
    showLoader.value = true;
    await intakeService.postUpdateOrderStatus(order.value.id, INTAKE_STAGE.QUALIFY_PATIENT);
    router.push({ name: "qualify-order", params: { id: order.value.id } });
  } catch (ex) {
    errorMsg.value = ex.message;
  } finally {
    showLoader.value = false;
  }
};

const onBack = () => {
  router.push(listReturnTarget({ name: "all-patients", query: filtersStore.getNavigationQuery() }));
};

const onRequestMissingInformation = () => {
  missingInformationVisible.value = !missingInformationVisible.value;
  if (missingInformationVisible.value) {
    referralThankYouVisible.value = false;
  }
};

const onReferralThankYou = () => {
  referralThankYouVisible.value = !referralThankYouVisible.value;
  if (referralThankYouVisible.value) {
    missingInformationVisible.value = false;
  }
};

const sendEmailEnabled = computed(() => requirementsStore.clientConfig?.intake?.sendEmailEnabled ?? false);
const missingInformationSubject = computed(() => {
  const name = [patient.value?.firstName, patient.value?.lastName].filter(Boolean).join(" ");
  return name ? `Request for Additional Information - ${name}` : "Request for Additional Information";
});

// The flag tells the send endpoint to tag the order "Requested Additional Information".
const markMissingInformationRequest = (requestData) => ({ ...requestData, missingInformationRequest: true });

// The send response carries the order's tags after the backend tagged it, so the new chip shows.
const onMissingInformationComplete = ({ sent }) => {
  missingInformationVisible.value = false;
  if (!sent) return;
  order.value.statusTags = sent.statusTags;
  auditLogListRef.value?.showLatest();
};

const onDataFormSelected = (dataForm) => {
  pageManager.goToDataFormPage(dataForm);
};

// Back to the intake list; depending on submission method (i.e. CoverMyMeds) some orders will
// require standby time before status will switch from Created to Requires Review
const backToList = () =>
  router.push(listReturnTarget({ name: "all-patients", query: filtersStore.getNavigationQuery() }));

// Only the draft run opens its PA; "PA not required" and "external medical" go back to the list as before
const afterSubmit = (pa, draftRun) => {
  if (draftRun && pa) router.push(getPARoute(pa.orderKey, pa.status));
  else backToList();
};

const resumePendingSubmit = async () => {
  const pending = await clinicalReviewSubmitService.pending(orderId, { isActive: () => !unmounted });
  if (unmounted || !pending) return;
  if (pending.status === "failed") {
    errorMsg.value = `The last submit did not finish: ${submitFailure(pending).message}`;
    return;
  }
  if (pending.status !== "processing") return;
  showLoader.value = true;
  buttonDisabled.value = true;
  try {
    const pa = await clinicalReviewSubmitService.wait(orderId, {
      isActive: () => !unmounted,
      submitId: pending.submitId,
    });
    // A pending submit is never a repeat (a repeat answers at once), so a draft run ends with its PA Initiated
    afterSubmit(pa, draftsPa.value && pa?.status === "Initiated");
  } catch (ex) {
    if (!(ex instanceof SubmitAbandoned)) errorMsg.value = `Error submitting the review: ${ex.message}`;
  } finally {
    showLoader.value = false;
    buttonDisabled.value = false;
  }
};

const formIsValid = async () => {
  if (await handleFormValidationErrors(previewRef.value.formErrors)) return true;
  errorMsg.value = "There are some errors with the document data";
  return false;
};

const onStartPriorAuth = async () => {
  errorMsg.value = "";
  // A draft PA is validated in Create Submission instead
  if (!draftsPa.value && !(await formIsValid())) return;
  if (restrictToPharmacy.value && orderBenefitType.value !== "PHARMACY") {
    medicalBenefitDialogVisible.value = true;
    return;
  }
  await submitStep("prior_auth");
};

const submitStep = async (disposition = "prior_auth", externalData = null, benefitType = null) => {
  showLoader.value = true;
  errorMsg.value = "";
  buttonDisabled.value = true;
  try {
    await order.value.saveUpdates();
    await waitForNetworkSelectionSave();
    const payload = { disposition };
    if (benefitType) {
      payload.benefitType = benefitType;
    }
    if (disposition === "external_medical" && externalData) {
      payload.externalPortal = externalData.portal;
      payload.externalReference = externalData.reference;
      payload.externalOutcome = externalData.outcome;
      payload.externalNotes = externalData.notes;
    }
    const pa = await clinicalReviewSubmitService.submit(order.value.id, payload, { isActive: () => !unmounted });
    afterSubmit(pa, draftsPa.value && disposition === "prior_auth");
    return true;
  } catch (ex) {
    if (!(ex instanceof SubmitAbandoned)) errorMsg.value = `Error submitting the review: ${ex.message}`;
    return false;
  } finally {
    showLoader.value = false;
    buttonDisabled.value = false;
  }
};

const onConfirmPharmacy = async () => {
  if (await submitStep("prior_auth", null, "PHARMACY")) {
    medicalBenefitDialogVisible.value = false;
  }
};

// These decisions make a validated PA, so they check the form a draft start skipped; the dialog closes to show the field
const submitMedicalDecision = async (disposition, externalData = null) => {
  if (draftsPa.value && !(await formIsValid())) {
    medicalBenefitDialogVisible.value = false;
    return;
  }
  if (await submitStep(disposition, externalData, "MEDICAL")) {
    medicalBenefitDialogVisible.value = false;
  }
};

const onMarkPaNotRequired = () => submitMedicalDecision("pa_not_required");

const onSubmitExternal = (externalData) => submitMedicalDecision("external_medical", externalData);

const fetchBenefitsStep = async () => {
  try {
    return await intakeService.getIntakeOrderBenefitsStep(orderId);
  } catch (e) {
    if (e?.response?.status !== 404) throw e;
    return null;
  }
};

const fetchIntakeOrder = async () => {
  showLoader.value = true;
  errorMsg.value = "";
  pageError.value = null;
  try {
    const orderData = await intakeService.getIntakeOrder(orderId);
    bvStep.value = orderData.insuranceCard ? null : await fetchBenefitsStep();
    const orderObj = new IntakeOrder(orderData, dateAdapter);
    intakeStore.intakeOrder = orderObj;
    intakeStore.insuranceCards = orderObj.insuranceCard ? [orderObj.insuranceCard] : [];
  } catch (ex) {
    if ([403, 404].includes(ex?.response?.status)) pageError.value = "This medical necessity review was not found.";
    else errorMsg.value = `Error fetching draft order: ${ex.message ?? ex}`;
  } finally {
    showLoader.value = false;
  }
};

const fetchPages = async () => {
  const request = ++pagesRequest;
  try {
    const pageResults = await intakeService.getPatientDocumentPages(order.value.patient.id, undefined, orderId);
    if (unmounted || request !== pagesRequest) return;
    const shownPageId = currentPage.value?.id;
    const shownFilter = intakeStore.pageManager.activeFilter;
    const pages = [];
    const orderFormPage = order.value.orderForm?.pages?.[0];
    if (orderFormPage) {
      pages.push(orderFormPage);
    }
    const insuranceCardPage = order.value.insuranceCard?.pages?.[0];
    if (insuranceCardPage && insuranceCardPage.id !== orderFormPage?.id) {
      pages.push(insuranceCardPage);
    }
    const pagesIds = pages.map((p) => p.id);
    // All pages except the order form and insurance card
    pageResults.results
      .filter((p) => !pagesIds.includes(p.id))
      .forEach((p) => pages.push(new IntakeDocumentPage(p, dateAdapter)));
    intakeStore.setPageManager(pages, currentPage);
    if (!pages.length && !isManualEntry.value) errorMsg.value = "No document pages found for this order.";
    const shownIndex = intakeStore.pageManager.getPageNumber(shownPageId);
    if (shownIndex > 0) intakeStore.pageManager.goToPage(shownIndex);
    if (shownFilter != null) intakeStore.pageManager.setFilter(shownFilter);
    pagesLoaded.value = true;
  } catch (ex) {
    if (unmounted || request !== pagesRequest) return;
    errorMsg.value = `Error fetching pages: ${ex.message ?? ex}`;
  } finally {
    if (!unmounted && request === pagesRequest) showLoader.value = false;
  }
};

const onAttributeEdit = (field, value, type) => {
  if (!mayEditForm.value) return;
  if (type === DOCUMENT_CLASSIFICATIONS.ORDER_FORM) {
    order.value?.orderForm.updateAttribute(field.key, value);
    order.value?.orderForm.debouncedSaveUpdates();
  } else if (type === DOCUMENT_CLASSIFICATIONS.INSURANCE_CARD) {
    order.value?.insuranceCard.updateAttribute(field.key, value);
    order.value?.insuranceCard.debouncedSaveUpdates();
  }
};

const onSnooze = async ({ minutes }) => {
  try {
    errorMsg.value = "";
    showLoader.value = true;
    const response = await intakeService.postSnoozeOrder(orderId, minutes);
    intakeStore.intakeOrder = new IntakeOrder(
      {
        ...response,
        reviewOrderInfo: order.value?.reviewOrderInfo ?? null,
        priorAuthHidden: order.value?.priorAuthHidden ?? false,
      },
      dateAdapter
    );
    if (minutes > 0) {
      router.push(listReturnTarget({ name: "all-patients", query: filtersStore.getNavigationQuery() }));
    }
  } catch (e) {
    errorMsg.value = `Error snoozing order: ${e.message}`;
  } finally {
    showLoader.value = false;
  }
};

const onArchive = async () => {
  if (order.value.status === INTAKE_STAGE.ARCHIVED) {
    try {
      await intakeService.deleteArchiveOrder(order.value.id);
      intakeStore.setReadOnly(false);
      return;
    } catch (e) {
      errorMsg.value = `Error unarchiving order: ${e.message}`;
    }
  }
  showArchiveReasonDialog.value = true;
};

const onArchived = () => {
  showArchiveReasonDialog.value = false;
  router.push(listReturnTarget({ name: "all-patients", query: filtersStore.getNavigationQuery() }));
};

const shouldShowReimbursementTable = computed(() => {
  // LAM-4074: hidden for all clients; restore the reimbursementTableEnabled config check to re-enable.
  return false;
});

const documentsShown = computed(
  () =>
    // Only a company with no intake flow adds files here; every other company adds files through intake.
    (requirementsStore.clientConfig?.intake?.intakeEnabled ?? true) === false &&
    Boolean(requirementsStore.clientConfig?.intake?.quickStartClinicalReviewEnabled) &&
    !viewOnly.value &&
    Boolean(order.value?.caseIsOpen)
);
const documentsAdded = ref(false);
// Batches still being checked. The review re-runs only once every batch has finished, not only the last one sent.
const documentsProcessing = ref(0);
const rerunningReview = ref(false);
const documentsRerunError = ref(null);
const documentsNotice = ref(null);
const DOCUMENT_TASK_NOTICES = {
  failed: { type: "error", text: "Some files could not be read. Upload them again." },
  [POLL_TIMED_OUT]: { type: "info", text: "The files are still processing. Reload the page to see them." },
  error: { type: "error", text: "Could not check the upload. Reload the page to see the files." },
};
// The rerun answers that start nothing; "queued" refreshes the policy review card instead.
const RERUN_NOTICES = {
  off: { type: "info", text: "There is no policy review to re-run on this order." },
  unchanged: { type: "info", text: "The policy review already read these files." },
};
// Each document's task runs in the background; one poll covers the upload's tasks. It ends when none is
// processing ("done" with the added and failed counts) or on timeout.
const { pollTask, clearPolls } = useTaskPoll(
  (taskIds) => intakeService.getPatientDocumentsTasks(patient.value.id, taskIds),
  { intervalMs: DOCUMENT_READY_POLL_MS, timeoutMs: DOCUMENT_READY_LIMIT_MS }
);
const onDocumentsUploaded = async (payload) => {
  documentsNotice.value = null;
  documentsProcessing.value += 1;
  const {
    status,
    added = 0,
    failed = 0,
  } = payload ? await pollTask(payload.taskIds).catch(() => ({ status: "error" })) : { status: "done", added: 1 };
  documentsProcessing.value -= 1;
  if (unmounted) return;
  // A failed document keeps the files the others added, so it shows its notice without hiding the re-run.
  if (added) documentsAdded.value = true;
  const problem = status === "done" ? (failed ? "failed" : null) : status;
  if (problem) documentsNotice.value = DOCUMENT_TASK_NOTICES[problem];
  await fetchPages();
};
const onRerunDocumentsReview = async () => {
  rerunningReview.value = true;
  documentsRerunError.value = null;
  try {
    const { review } = await carequalityService.rerunNetworkReview(orderId);
    documentsAdded.value = false;
    documentsNotice.value = RERUN_NOTICES[review] ?? null;
    await onEvidenceChanged({ processing: 0, review });
  } catch {
    documentsRerunError.value = "The policy review did not restart. Try again.";
  } finally {
    rerunningReview.value = false;
  }
};

const shouldShowPolicyReview = computed(() => {
  return requirementsStore.clientConfig?.intake?.policyReviewEnabled ?? false;
});
// The rerun endpoint answers "off" for a company without policy review, so the button is not offered there.
const rerunShown = computed(() => documentsAdded.value && !documentsProcessing.value && shouldShowPolicyReview.value);

const emptyEvidenceText = computed(() =>
  shouldShowPolicyReview.value
    ? "No documents for this patient yet. The policy review reads the order details only."
    : "No documents for this patient yet."
);

const carequalityEnabled = computed(() => {
  return requirementsStore.clientConfig?.intake?.carequalityEnabled ?? false;
});
</script>

<style lang="scss" scoped>
@import "@/styles/pages/_patient-intake-review.scss";

@keyframes network-badge-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}

.network-badge--running :deep(.v-badge__badge) {
  animation: network-badge-pulse 1.2s ease-in-out infinite;
}

@media (prefers-reduced-motion: reduce) {
  .network-badge--running :deep(.v-badge__badge) {
    animation: none;
  }
}
</style>
